import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload, useBlocker, useNavigate } from "react-router";

type EntityFormLeaveGuardOptions = {
    dirty: boolean;
    busy: boolean;
};

/** Protects entity forms across explicit links, router history and browser unload. */
export const useEntityFormLeaveGuard = (options: EntityFormLeaveGuardOptions) => {
    const { dirty, busy } = options;
    const navigate = useNavigate();
    const [destination, setDestination] = useState<string | null>(null);
    const [savingForExit, setSavingForExit] = useState(false);
    const allowNextNavigation = useRef(false);
    const blocker = useBlocker(
        useCallback(() => {
            if (allowNextNavigation.current) {
                allowNextNavigation.current = false;
                return false;
            }
            return dirty || busy;
        }, [dirty, busy]),
    );
    const latestBlocker = useRef(blocker);
    useEffect(() => {
        latestBlocker.current = blocker;
    }, [blocker]);

    useBeforeUnload(
        useCallback(
            (event) => {
                if (!dirty && !busy) return;
                event.preventDefault();
                event.returnValue = "";
            },
            [dirty, busy],
        ),
    );

    const keepEditing = () => {
        setDestination(null);
        setSavingForExit(false);
        if (latestBlocker.current.state === "blocked") latestBlocker.current.reset();
    };

    const completeSave = (fallbackDestination: string) => {
        setDestination(null);
        setSavingForExit(false);
        if (latestBlocker.current.state === "blocked") {
            latestBlocker.current.proceed();
        } else {
            allowNextNavigation.current = true;
            navigate(destination ?? fallbackDestination);
        }
    };

    const requestExit = useCallback(
        (nextDestination: string) => {
            if (busy) return;
            if (dirty) setDestination(nextDestination);
            else navigate(nextDestination);
        },
        [busy, dirty, navigate],
    );

    return {
        open: (destination !== null || blocker.state === "blocked") && !savingForExit && !busy,
        requestExit,
        keepEditing,
        beginSave: () => setSavingForExit(true),
        cancelSave: keepEditing,
        completeSave,
        discard: () => {
            if (busy || (destination === null && blocker.state !== "blocked")) return;
            completeSave(destination ?? "");
        },
    };
};
